// src/services/roleService.js
import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

// Safely unwrap apiClient response — handles both raw array and axios-style { data: [] }
function unwrapList(response) {
  if (Array.isArray(response))       return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

// Safely unwrap a single object response
function unwrapOne(response) {
  return response?.data ?? response;
}

export const roleService = {

  getAllRoles: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.ROLES.GET_ALL);
      const data = unwrapList(response);
      if (process.env.NODE_ENV === "development" && data[0]) {
        console.log("[roleService] getAllRoles — sample permissions:", data[0].permissions?.slice(0, 2));
      }
      return data;
    } catch (error) {
      console.error('[roleService] getAllRoles failed:', error);
      throw error;
    }
  },

  getRoleById: async (roleId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.ROLES.GET_BY_ID(roleId));
      return unwrapOne(response);
    } catch (error) {
      console.error(`[roleService] getRoleById(${roleId}) failed:`, error);
      throw error;
    }
  },

  createRole: async (roleData) => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("[roleService] createRole — payload:", JSON.stringify({
          ...roleData,
          permissions: roleData.permissions?.slice(0, 3), // log first 3 perms to keep it readable
        }, null, 2));
        const missing = roleData.permissions?.filter((p) => !("has_permission" in p));
        if (missing?.length) console.warn("[roleService] createRole — permissions missing has_permission:", missing);
      }
      const response = await apiClient.post(ENDPOINTS.ROLES.CREATE, roleData);
      return unwrapOne(response);
    } catch (error) {
      console.error('[roleService] createRole failed:', error);
      throw error;
    }
  },

  updateRole: async (roleId, roleData) => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log(`[roleService] updateRole(${roleId}) — payload:`, JSON.stringify({
          ...roleData,
          permissions: roleData.permissions?.slice(0, 3),
        }, null, 2));
        const missing = roleData.permissions?.filter((p) => !("has_permission" in p));
        if (missing?.length) console.warn("[roleService] updateRole — permissions missing has_permission:", missing);
      }
      const response = await apiClient.put(ENDPOINTS.ROLES.UPDATE(roleId), roleData);
      return unwrapOne(response);
    } catch (error) {
      console.error(`[roleService] updateRole(${roleId}) failed:`, error);
      throw error;
    }
  },

  deleteRole: async (roleId) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.ROLES.DELETE(roleId));
      return unwrapOne(response);
    } catch (error) {
      console.error(`[roleService] deleteRole(${roleId}) failed:`, error);
      throw error;
    }
  },

  getAllPermissions: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.PERMISSIONS.GET_ALL);
      const data = unwrapList(response);
      if (process.env.NODE_ENV === "development") {
        console.log("[roleService] getAllPermissions — sample:", data.slice(0, 2));
      }
      return data;
    } catch (error) {
      console.error('[roleService] getAllPermissions failed:', error);
      throw error;
    }
  },

  groupPermissionsByGroup: (permissions = []) => {
    return permissions.reduce((acc, perm) => {
      const group = perm.groupName || 'General';
      if (!acc[group]) acc[group] = [];
      acc[group].push(perm);
      return acc;
    }, {});
  },
};

export default roleService;